import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { customerPublicCopy } from "../lib/customerPublicCopy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_PREFIX = "cargo_site_content::";

// Module-level cache so SPA navigation doesn't re-fetch site-wide content
// (footer/header) on every page change.
const _memoryCache = new Map();

function _readSession(slug) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + slug);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function _writeSession(slug, value) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify(value));
  } catch (e) {
    // sessionStorage may be unavailable (private mode etc.) — silent UX but log for debug
    console.warn("useSiteContent: sessionStorage write failed for", slug, e);
  }
}

/**
 * Fetch a published CMS page once per session and reuse it across components.
 * Returns the published `content.blocks` array or null when the page is
 * missing/archived/empty/erroring (caller renders hardcoded fallback).
 *
 * Behaviour:
 *  - Hydrates synchronously from sessionStorage so first paint is immediate
 *    when the user returns or navigates between pages.
 *  - Refreshes the cache asynchronously in the background — silent on error.
 */
export default function useSiteContent(slug) {
  const initial = _memoryCache.get(slug) || _readSession(slug) || null;
  const [state, setState] = useState({
    blocks: initial?.blocks || null,
    seo: initial?.seo || null,
    loading: !initial,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND_URL}/api/website/pages/${slug}`)
      .then((res) => {
        if (!alive) return;
        const page = res.data;
        if (!page || page.status !== "published") {
          // Treat as fallback territory
          setState({ blocks: null, seo: page?.seo || null, loading: false, error: null });
          return;
        }
        const blocks = (page.content?.blocks || []).filter(
          (b) => b && b.active !== false
        );
        const cached = { blocks, seo: page.seo || null };
        _memoryCache.set(slug, cached);
        _writeSession(slug, cached);
        setState({ blocks, seo: page.seo || null, loading: false, error: null });
      })
      .catch((err) => {
        if (!alive) return;
        // Soft fail — caller will render legacy fallback
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[useSiteContent] /api/website/pages/${slug} failed:`, err?.message);
        }
        setState((s) => ({ ...s, loading: false, error: err }));
      });
    return () => { alive = false; };
  }, [slug]);

  return useMemo(() => customerPublicCopy(state), [state]);
}

/** Return the LAST active block of a given type — admins typically append
 * new blocks to the end of the page, so picking the last active match
 * exposes the most-recently-edited version. Returns null when absent. */
export function findBlock(blocks, type) {
  if (!Array.isArray(blocks)) return null;
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const b = blocks[i];
    if (b && b.type === type && b.active !== false) return b;
  }
  return null;
}
